import { useEffect, useState } from "react";
import { Bot, GitBranch, Loader2, PlusCircle, User, X } from "lucide-react";
import { createPortal } from "react-dom";

const NODE_TYPES = [
    { value: "manual", label: "Manual task", description: "Completed by a salesperson", icon: User },
    { value: "decision", label: "Decision", description: "Creates one or more paths", icon: GitBranch },
    { value: "automation", label: "Automation", description: "Performed by the system", icon: Bot },
];

export default function AddNodeDialog({
    open,
    parentNode,
    stageId,
    initialData = null,
    isEdit = false,
    isBranch = false,
    onClose,
    onSave,
}) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        node_type: "manual",
        can_skip: false,
        transition_label: "",
        metadata: {},
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            name: initialData?.name || "",
            description: initialData?.description || "",
            node_type: initialData?.node_type || "manual",
            can_skip: Boolean(initialData?.can_skip),
            transition_label: "",
            metadata: initialData?.metadata || {},
        });
        setSubmitting(false);
    }, [initialData, open]);

    if (!open) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.name.trim() || submitting) return;

        setSubmitting(true);
        try {
            await onSave?.({
                stage_id: stageId,
                parent_node_id: parentNode?.id,
                name: form.name.trim(),
                description: form.description.trim(),
                node_type: form.node_type,
                can_skip: form.can_skip,
                action_group_name: form.transition_label.trim() || null,
                metadata: form.metadata,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const title = isEdit ? "Edit action" : isBranch ? "Add decision path" : parentNode ? "Add next action" : "Add action";
    const subtitle = isEdit
        ? "Update how this workflow action behaves."
        : parentNode
            ? `Continue after “${parentNode.name}”.`
            : "Add a new starting action to this stage.";

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#071321]/45 p-4 backdrop-blur-sm" onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
            <div role="dialog" aria-modal="true" aria-labelledby="workflow-node-dialog-title" className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/60 bg-white shadow-[0_30px_90px_rgba(15,43,92,0.24)]">
                <header className="flex items-center justify-between border-b border-[#e4edf5] px-6 py-5">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#e8f6fd] text-[#147db5]"><PlusCircle className="h-5 w-5" /></span>
                        <div>
                            <h2 id="workflow-node-dialog-title" className="text-lg font-bold text-[#102b5c]">{title}</h2>
                            <p className="mt-0.5 text-sm text-[#7185a3]">{subtitle}</p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close action editor" className="flex h-9 w-9 items-center justify-center rounded-xl text-[#7185a3] transition hover:bg-[#f1f6fb] hover:text-[#294d63]"><X className="h-[18px] w-[18px]" /></button>
                </header>

                <form onSubmit={handleSubmit} className="space-y-5 p-6">
                    <div>
                        <label htmlFor="workflow-action-name" className="mb-1.5 block text-xs font-bold text-[#294d63]">Action name</label>
                        <input id="workflow-action-name" autoFocus value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Verify contact details" maxLength={150} required className="h-11 w-full rounded-xl border border-[#cdddea] px-3.5 text-sm text-[#173552] outline-none transition placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                    </div>

                    <div>
                        <label htmlFor="workflow-action-description" className="mb-1.5 block text-xs font-bold text-[#294d63]">Instruction</label>
                        <textarea id="workflow-action-description" value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Describe what must be completed before moving forward." rows={3} className="w-full resize-none rounded-xl border border-[#cdddea] px-3.5 py-3 text-sm leading-6 text-[#173552] outline-none transition placeholder:text-[#9bacc0] focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                    </div>

                    <fieldset>
                        <legend className="mb-2 block text-xs font-bold text-[#294d63]">Action type</legend>
                        <div className="grid grid-cols-3 gap-2.5">
                            {NODE_TYPES.map((type) => {
                                const Icon = type.icon;
                                const selected = form.node_type === type.value;
                                return (
                                    <label key={type.value} className={`cursor-pointer rounded-2xl border p-3 transition ${selected ? "border-[#72bbe0] bg-[#edf8fe] ring-2 ring-[#dff3fc]" : "border-[#dce7f1] hover:border-[#b9dcef]"}`}>
                                        <input type="radio" name="node_type" value={type.value} checked={selected} onChange={(event) => setForm((current) => ({ ...current, node_type: event.target.value }))} className="sr-only" />
                                        <Icon className={`h-[18px] w-[18px] ${selected ? "text-[#147db5]" : "text-[#7185a3]"}`} />
                                        <span className="mt-2 block text-xs font-bold text-[#173552]">{type.label}</span>
                                        <span className="mt-0.5 block text-[10px] leading-4 text-[#7b8da9]">{type.description}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>

                    {parentNode && !isEdit && (
                        <div className="rounded-2xl border border-[#dce8f3] bg-[#f7fbff] p-4">
                            <label className="mb-1.5 block text-xs font-bold text-[#294d63]">{isBranch ? "Outcome label" : "Connection label"}</label>
                            <input value={form.transition_label} onChange={(event) => setForm((current) => ({ ...current, transition_label: event.target.value }))} placeholder={isBranch ? "e.g. Qualified, No answer, Not interested" : "e.g. Continue"} maxLength={100} className="h-10 w-full rounded-xl border border-[#cdddea] bg-white px-3 text-sm text-[#173552] outline-none focus:border-[#49a9d8] focus:ring-2 focus:ring-[#dff3fc]" />
                            <p className="mt-1.5 text-[11px] leading-5 text-[#7b8da9]">{isBranch ? "Use a clear outcome so salespeople know which path to choose." : "Optional text displayed on the line between actions."}</p>
                        </div>
                    )}

                    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#dce8f3] p-3.5 transition hover:bg-[#f8fbfe]">
                        <input type="checkbox" checked={form.can_skip} onChange={(event) => setForm((current) => ({ ...current, can_skip: event.target.checked }))} className="mt-0.5 h-4 w-4 rounded border-[#b9cad9] text-[#147db5] focus:ring-[#49a9d8]" />
                        <span><span className="block text-xs font-bold text-[#294d63]">Allow skipping</span><span className="mt-0.5 block text-[11px] text-[#7b8da9]">Salespeople can move ahead without completing this action.</span></span>
                    </label>

                    <div className="flex justify-end gap-2.5 border-t border-[#edf2f7] pt-5">
                        <button type="button" onClick={onClose} className="h-10 rounded-xl border border-[#d5e4f2] bg-white px-4 text-sm font-bold text-[#607b9f] hover:bg-[#f5f9fd]">Cancel</button>
                        <button type="submit" disabled={submitting || !form.name.trim()} className="inline-flex h-10 min-w-[120px] items-center justify-center gap-2 rounded-xl bg-[#147db5] px-4 text-sm font-bold text-white transition hover:bg-[#0f6f9f] disabled:cursor-not-allowed disabled:opacity-60">{submitting && <Loader2 className="h-4 w-4 animate-spin" />}{isEdit ? "Save changes" : isBranch ? "Create path" : "Add action"}</button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
